import React, {useEffect, useState} from 'react';
import {Modal, Button, Textarea, Input, Checkbox} from '@salesforce/design-system-react';
import { isValidUrl } from '../../../../components/utils/common';

type CreateReasonModalProps = {
    isOpen: boolean;
    toggleModal: (isOpen: boolean) => void;
    onCancel: () => void;
    onCreate: (data: ReasonData) => void;
    majorReason?: any
};

type ReasonData = {
    label: string;
    description: string;
    url: string;
    note: string;
    sort_order?: number;
    is_active: boolean;
    show_recommended_actions: boolean;
};

const CreateReasonModal: React.FC<CreateReasonModalProps> = ({
                                                                 isOpen,
                                                                 toggleModal,
                                                                 onCancel,
                                                                 onCreate,
                                                                 majorReason,
                                                             }) => {
    const [label, setLabel] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [url, setUrl] = useState<string>('');
    const [note, setNote] = useState<string>('');
    const [sort_order, setSortOrder] = useState<number>(0);
    const [show_recommended_actions, setShowRecommendedActions] = useState<boolean>(false);

    const [urlError, setUrlError] = useState<string>("");
    const [isCreateDisabled, setIsCreateDisabled] = useState(true);

    useEffect(() => {
        setLabel("");
        setDescription("");
        setUrl("");
        setNote("");
        setUrlError("");
        setSortOrder(0);
        setShowRecommendedActions(!!majorReason);
    }, [isOpen, majorReason]);

    const handleCreate = () => {
        toggleModal(false);
        let newValue = label
        let type = "MAJOR"
        let parentId = ""
        if (majorReason) {
            newValue = majorReason.value + " - " + label
            type = "MINOR"
            parentId = majorReason.id
        }
        const data = {
            label: label,
            value: newValue,
            description: description,
            url: url,
            note: note,
            type: type,
            parent_id: parentId,
            sort_order: sort_order,
            is_active: true,
            show_recommended_actions: show_recommended_actions,
        }
        onCreate(data);
    };

    const handleLabelChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setLabel(event.target.value);
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(event.target.value);
    };

    const handleURLChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setUrl(event.target.value);
    };

    const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNote(event.target.value);
    };

    const handleSortOrderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSortOrder(parseInt(event.target.value) || 0);
    };



    // Run validation on input change
    useEffect(() => {
        const urlValidation = isValidUrl(url || '');
        setUrlError(urlValidation.errorMessage);
        const allValid = label.trim() && description.trim() && urlValidation.isValid;
        setIsCreateDisabled(!allValid);
    }, [label, description, url]);

    const modalTitle = majorReason ? 'Create Minor Reason' : 'Create Major Reason';

    return (
        <Modal
            isOpen={isOpen}
            disableClose
            prompt="success"
            onRequestClose={() => toggleModal(false)}
            heading={modalTitle}
            footer={[
                <Button key="create" label="Create" variant="brand" onClick={handleCreate} disabled={isCreateDisabled}/>,
                <Button key="cancel" label="Cancel" variant="neutral" onClick={onCancel} />,
            ]}
        >
            <div className="slds-p-around_medium slds-form slds-form_stacked">
                {majorReason && (
                    <div>
                        <div className="slds-form-element slds-m-bottom_medium">
                            <label className="slds-form-element__label">Major Reason</label>
                            <div className="slds-form-element__control">
                                <span className="slds-text-body_regular slds-p-around_xx-small slds-theme_shade">
                                    {majorReason.label}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
                <div className="slds-p-around--xx-small">
                    <Input
                        id="create_reason_label"
                        label="Label"
                        value={label}
                        required={true}
                        onChange={handleLabelChange}
                        placeholder="Enter a valid label"
                    />
                    <Textarea
                        id="create_reason_description"
                        label="Description"
                        value={description}
                        required={true}
                        onChange={handleDescriptionChange}
                        placeholder="Enter a valid description"
                    />
                    {majorReason && (
                        <div>
                            <Input
                                id="create_reason_url"
                                label="URL"
                                value={url}
                                onChange={handleURLChange}
                                type="url"
                                placeholder="Enter a valid URL"
                                errorText={urlError}
                            />
                            <Textarea
                                id="create_reason_note"
                                label="Note"
                                value={note}
                                onChange={handleNoteChange}
                                placeholder="Enter a valid note"
                            />
                        </div>
                )}
                    <Input
                        id="create_reason_sort_order"
                        label="Sort Order"
                        value={sort_order}
                        onChange={handleSortOrderChange}
                        type="number"
                        placeholder="Enter sort order"
                    />
                    {majorReason && (
                        <Checkbox
                            assistiveText={{
                                label: 'Show Recommended Actions',
                            }}
                            id="create_reason_show_recommended_actions"
                            labels={{
                                label: 'Show Recommended Actions',
                            }}
                            onChange={(e: any) => {
                                if (e.target.checked) {
                                    setShowRecommendedActions(true)
                                } else {
                                    setShowRecommendedActions(false)
                                }
                            }}
                            checked={show_recommended_actions}
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default CreateReasonModal;